import posthog from "posthog-js";

const DEFAULT_POSTHOG_HOST = "https://us.i.posthog.com";
const DEFAULT_POSTHOG_SERVICE = "web";
const POSTHOG_STATE_KEY = "__minCoolPostHogState";
const SENSITIVE_TELEMETRY_PARAMS = new Set([
  "access_token",
  "authorization_id",
  "code",
  "handoff",
  "refresh_token",
  "sb",
  "token",
  "t",
]);

let posthogClient = posthog;
let enabledOverride = null;
let appEnvOverride = "";
let runtimeEnvOverride = null;

function runtimeEnv() {
  if (runtimeEnvOverride && typeof runtimeEnvOverride === "object") {
    return runtimeEnvOverride;
  }
  return import.meta.env || {};
}

function envValue(name) {
  const value = runtimeEnv()[name];
  return typeof value === "string" ? value : "";
}

function getPostHogState() {
  if (typeof window === "undefined") {
    return {
      initialized: false,
      bridgeRegistered: false,
      identifiedUserId: "",
      identifiedEmail: "",
      accountId: "",
    };
  }

  if (!window[POSTHOG_STATE_KEY]) {
    window[POSTHOG_STATE_KEY] = {
      initialized: false,
      bridgeRegistered: false,
      identifiedUserId: "",
      identifiedEmail: "",
      accountId: "",
    };
  }

  return window[POSTHOG_STATE_KEY];
}

function safeCall(fn, fallback = "") {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

function telemetryBaseUrl() {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return "https://min.cool";
}

function shouldRedactTelemetryParam(key = "") {
  const normalized = String(key || "").trim().toLowerCase().replace(/[-.]/g, "_");
  return SENSITIVE_TELEMETRY_PARAMS.has(normalized)
    || normalized.startsWith("expires_")
    || normalized.startsWith("provider_")
    || normalized.endsWith("_token");
}

function sanitizeReturnToParam(value = "") {
  const normalized = String(value || "").trim();
  if (!normalized.startsWith("/") || normalized.startsWith("//")) {
    return normalized;
  }
  return sanitizeTelemetryUrl(normalized);
}

export function sanitizeTelemetryUrl(value = "") {
  const rawValue = String(value || "");
  if (!rawValue) {
    return rawValue;
  }
  const absoluteInput = /^[a-z][a-z\d+\-.]*:\/\//i.test(rawValue);
  try {
    const url = new URL(rawValue, telemetryBaseUrl());
    url.hash = "";
    url.pathname = url.pathname.replace(/^\/m\/[^/]+\/?$/, "/m/[redacted]");
    for (const key of [...url.searchParams.keys()]) {
      if (shouldRedactTelemetryParam(key)) {
        url.searchParams.set(key, "[redacted]");
      } else if (String(key || "").toLowerCase() === "returnto") {
        url.searchParams.set(key, sanitizeReturnToParam(url.searchParams.get(key) || ""));
      }
    }
    return absoluteInput
      ? `${url.origin}${url.pathname}${url.search}`
      : `${url.pathname}${url.search}`;
  } catch {
    return rawValue.split("#")[0];
  }
}

export function sanitizeTelemetryRoute(locationLike = null) {
  const locationSource = locationLike || (typeof window !== "undefined" ? window.location : null);
  const pathname = String(locationSource?.pathname || "/");
  const search = String(locationSource?.search || "");
  return sanitizeTelemetryUrl(`${pathname}${search}`);
}

function shouldSanitizeTelemetryString(key = "", value = "") {
  const normalizedKey = String(key || "").trim().toLowerCase();
  const normalizedValue = String(value || "");
  return normalizedKey === "$current_url"
    || normalizedKey === "$referrer"
    || normalizedKey.includes("url")
    || normalizedKey.includes("href")
    || normalizedKey.includes("pathname")
    || /(?:[?#&])(access_token|authorization_id|code|handoff|provider[-_]?token|refresh_token|sb|token)=/i.test(normalizedValue);
}

function sanitizeTelemetryValue(value, key = "", depth = 0) {
  if (typeof value === "string") {
    if ((key === "code" || key === "authorization_id") && value) return "[redacted]";
    return shouldSanitizeTelemetryString(key, value) ? sanitizeTelemetryUrl(value) : value;
  }
  if (!value || typeof value !== "object" || depth > 4) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((entry) => sanitizeTelemetryValue(entry, key, depth + 1));
  }
  return Object.fromEntries(
    Object.entries(value).map(([entryKey, entryValue]) => [
      entryKey,
      sanitizeTelemetryValue(entryValue, entryKey, depth + 1),
    ]),
  );
}

export function sanitizePostHogEvent(event) {
  if (isMcpAuthorizationSurface()) return null;
  return sanitizeTelemetryValue(event);
}

export function isMcpAuthorizationSurface(locationLike = null) {
  const source = locationLike || (typeof window !== "undefined" ? window.location : null);
  const sensitivePath = /^\/agent\/(?:(?:upstream-)?authorize|setup|(?:keys|media)\/[^/]+)\/?$/;
  if (sensitivePath.test(source?.pathname || "")) return true;
  const params = new URLSearchParams(source?.search || "");
  return sensitivePath.test(String(params.get("returnTo") || "").split("?")[0]);
}

export function stopSensitivePostHogRecording(locationLike) {
  if (isPostHogEnabled() && isMcpAuthorizationSurface(locationLike)) {
    safeCall(() => posthogClient.stopSessionRecording());
  }
}

export function resolvePostHogAppEnv() {
  const explicit = String(appEnvOverride || envValue("VITE_PUBLIC_APP_ENV") || "").trim().toLowerCase();
  if (explicit) {
    return explicit;
  }
  return String(envValue("MODE") || "development").trim().toLowerCase() || "development";
}

function defaultEventProperties() {
  const appEnv = resolvePostHogAppEnv();
  const properties = {
    app_env: appEnv,
    environment: appEnv,
    app_runtime: "web",
    service: DEFAULT_POSTHOG_SERVICE,
  };
  const appVersion = String(
    envValue("VITE_VERCEL_GIT_COMMIT_SHA")
    || envValue("VITE_GIT_COMMIT_SHA")
    || "",
  ).trim();
  if (appVersion) {
    properties.app_version = appVersion;
  }
  return properties;
}

function buildPersonProperties({ email = "", accountId = "" } = {}) {
  const properties = {
    ...defaultEventProperties(),
    last_seen_path: typeof window !== "undefined" ? window.location.pathname : "",
  };
  if (email) {
    properties.email = email;
  }
  if (accountId) {
    properties.account_id = accountId;
  }
  return properties;
}

export function isPostHogEnabled() {
  if (typeof enabledOverride === "boolean") {
    return enabledOverride;
  }
  return (
    Boolean(envValue("VITE_PUBLIC_POSTHOG_PROJECT_TOKEN").trim())
    && ["production", "preview"].includes(resolvePostHogAppEnv())
  );
}

export function initPostHog() {
  if (typeof window === "undefined") {
    return;
  }
  if (window.location?.pathname === "/pay" || isMcpAuthorizationSurface()) return;

  const state = getPostHogState();

  if (state.initialized || !isPostHogEnabled()) {
    return;
  }

  posthogClient.init(envValue("VITE_PUBLIC_POSTHOG_PROJECT_TOKEN"), {
    api_host: envValue("VITE_PUBLIC_POSTHOG_HOST").trim() || DEFAULT_POSTHOG_HOST,
    autocapture: false,
    capture_pageview: "history_change",
    capture_pageleave: true,
    capture_exceptions: true,
    before_send: sanitizePostHogEvent,
    defaults: "2026-01-30",
    session_recording: {
      maskAllInputs: true,
      recordHeaders: false,
      recordBody: false,
    },
    loaded: (client) => {
      client.register(defaultEventProperties());
      if (runtimeEnv().DEV) {
        client.debug();
      }
    },
  });

  state.initialized = true;
}

export function registerPostHogAnalyticsBridge() {
  if (typeof window === "undefined") {
    return;
  }

  const state = getPostHogState();

  if (state.bridgeRegistered || !isPostHogEnabled()) {
    return;
  }

  window.addEventListener("min-cool:analytics", (event) => {
    if (isMcpAuthorizationSurface()) return;
    const detail = event.detail || {};
    const eventName = typeof detail.eventName === "string" ? detail.eventName : "";
    const properties =
      detail.properties && typeof detail.properties === "object" ? detail.properties : {};

    if (!eventName) {
      return;
    }

    posthogClient.capture(eventName, {
      ...defaultEventProperties(),
      ...properties,
    });
  });

  state.bridgeRegistered = true;
}

export function getPostHogTracingHeaders() {
  if (!isPostHogEnabled() || isMcpAuthorizationSurface()) {
    return {};
  }
  const headers = {};
  const distinctId = String(safeCall(() => posthogClient.get_distinct_id(), "") || "").trim();
  const sessionId = String(safeCall(() => posthogClient.get_session_id(), "") || "").trim();
  if (distinctId) {
    headers["X-POSTHOG-DISTINCT-ID"] = distinctId;
  }
  if (sessionId) {
    headers["X-POSTHOG-SESSION-ID"] = sessionId;
  }
  return headers;
}

export function identifyPostHogUser(user, options = {}) {
  if (!isPostHogEnabled() || !user) {
    return;
  }

  const state = getPostHogState();
  const userId = String(user.id || options.userId || "").trim();
  if (!userId) {
    return;
  }
  const email = String(options.email || user.email || user.new_email || "").trim();
  const accountId = String(options.accountId || user.accountId || state.accountId || "").trim();
  const shouldUpdate =
    state.identifiedUserId !== userId
    || state.identifiedEmail !== email
    || state.accountId !== accountId;

  if (!shouldUpdate) {
    return;
  }

  posthogClient.identify(
    userId,
    buildPersonProperties({ email, accountId }),
  );
  state.identifiedUserId = userId;
  state.identifiedEmail = email;
  state.accountId = accountId;
}

export function setPostHogAccount(accountId) {
  if (!isPostHogEnabled()) {
    return;
  }
  const state = getPostHogState();
  const normalizedAccountId = String(accountId || "").trim();
  if (!normalizedAccountId || state.accountId === normalizedAccountId) {
    return;
  }
  state.accountId = normalizedAccountId;
  if (state.identifiedUserId) {
    posthogClient.identify(
      state.identifiedUserId,
      buildPersonProperties({
        email: state.identifiedEmail,
        accountId: normalizedAccountId,
      }),
    );
  }
}

export function resetPostHogUser() {
  if (!isPostHogEnabled()) {
    return;
  }
  const state = getPostHogState();
  posthogClient.reset();
  posthogClient.register(defaultEventProperties());
  state.identifiedUserId = "";
  state.identifiedEmail = "";
  state.accountId = "";
}

export function capturePostHogException(error, properties = {}) {
  if (!isPostHogEnabled()) {
    return;
  }
  const normalizedError =
    error instanceof Error ? error : new Error(String(error || "Unknown PostHog exception"));
  posthogClient.captureException(normalizedError, {
    ...defaultEventProperties(),
    ...properties,
  });
}

export function __setPostHogTestOverrides({ client = null, enabled = null, appEnv = "" } = {}) {
  if (client) {
    posthogClient = client;
  }
  enabledOverride = typeof enabled === "boolean" ? enabled : null;
  appEnvOverride = String(appEnv || "").trim().toLowerCase();
}

export function __setPostHogTestEnv(env = null) {
  runtimeEnvOverride = env && typeof env === "object" ? env : null;
}

export function __resetPostHogTestOverrides() {
  posthogClient = posthog;
  enabledOverride = null;
  appEnvOverride = "";
  runtimeEnvOverride = null;
  if (typeof window !== "undefined" && window[POSTHOG_STATE_KEY]) {
    delete window[POSTHOG_STATE_KEY];
  }
}
